import { Currency } from '@kodiak-finance/sdk-core'
import ERC20_ABI from 'abis/erc20.json'
import { BGT_ADDRESSES } from 'constants/addresses'
import { useConnection } from 'hooks/useConnection'
import { useSendTransaction, useWriteContract } from 'hooks/useTransaction'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { useCallback, useMemo, useState } from 'react'
import { isAddress } from 'utils'
import { maxAmountSpend } from 'utils/maxAmountSpend'
import { isUserRejection } from 'utils/userRejection'
import { Address, zeroAddress } from 'viem'

import { useTransactionAdder } from '../state/transactions/hooks'
import { TransactionType } from '../state/transactions/types'

export enum SendTokenInputError {
  NO_ERROR,
  NON_TRANSFERABLE,
  ENTER_AMOUNT,
  ENTER_RECIPIENT,
  INVALID_RECIPIENT,
  INSUFFICIENT_BALANCE,
}

interface UseSendTokenResult {
  execute: (() => Promise<string>) | undefined
  isPending: boolean
  inputError: SendTokenInputError | undefined
}

export function useSendToken(
  currency: Currency | null | undefined,
  recipient: string,
  amount: string,
  balance: string | undefined
): UseSendTokenResult {
  const { chainId, account } = useConnection()
  const addTransaction = useTransactionAdder()
  const [isPending, setIsPending] = useState(false)

  const parsedAmount = useMemo(() => tryParseCurrencyAmount(amount, currency ?? undefined), [currency, amount])

  const hasAmount = Boolean(parsedAmount?.greaterThan('0'))

  const isNonTransferableToken = useMemo(() => {
    if (!currency?.isToken) return false
    const bgtAddress = BGT_ADDRESSES[chainId]?.toLowerCase()
    return Boolean(bgtAddress && currency.wrapped.address.toLowerCase() === bgtAddress)
  }, [currency, chainId])

  const isValidRecipient = useMemo(() => {
    const trimmed = recipient.trim()
    if (!isAddress(trimmed)) return false
    if (trimmed.toLowerCase() === zeroAddress) return false
    if (trimmed.toLowerCase() === account?.toLowerCase()) return false
    return true
  }, [recipient, account])

  const sufficientBalance = useMemo(() => {
    if (!parsedAmount || !balance || !currency) return false
    try {
      const balanceAmount = tryParseCurrencyAmount(balance, currency)
      if (!balanceAmount) return false
      const maxSpendAmount = currency.isNative ? maxAmountSpend(balanceAmount) : balanceAmount
      return Boolean(maxSpendAmount && !maxSpendAmount.lessThan(parsedAmount))
    } catch {
      return false
    }
  }, [parsedAmount, balance, currency])

  const inputError = useMemo(() => {
    if (isNonTransferableToken) return SendTokenInputError.NON_TRANSFERABLE
    if (!hasAmount) return SendTokenInputError.ENTER_AMOUNT
    if (!recipient) return SendTokenInputError.ENTER_RECIPIENT
    if (!isValidRecipient) return SendTokenInputError.INVALID_RECIPIENT
    if (!sufficientBalance) return SendTokenInputError.INSUFFICIENT_BALANCE
    return SendTokenInputError.NO_ERROR
  }, [isNonTransferableToken, hasAmount, recipient, isValidRecipient, sufficientBalance])

  const { sendTransactionAsync } = useSendTransaction()
  const { writeContractAsync } = useWriteContract()

  const execute = useCallback(async () => {
    if (!currency || !parsedAmount || !account || !chainId || !isValidRecipient || isNonTransferableToken) {
      throw new Error('Missing dependencies')
    }

    setIsPending(true)

    try {
      let hash: string

      if (currency.isNative) {
        hash = await sendTransactionAsync({
          to: recipient as Address,
          value: BigInt(parsedAmount.quotient.toString()),
        })
      } else {
        const tokenAddress = currency.wrapped.address as Address
        hash = await writeContractAsync({
          abi: ERC20_ABI,
          address: tokenAddress,
          functionName: 'transfer',
          args: [recipient as Address, BigInt(parsedAmount.quotient.toString())],
        })
      }

      addTransaction({ hash } as any, {
        type: TransactionType.SEND_TOKEN,
        tokenAddress: currency.isNative ? 'native' : currency.wrapped.address,
        tokenSymbol: currency.symbol ?? 'Unknown',
        recipient,
        amount: parsedAmount.toExact(),
        isNative: currency.isNative,
      })

      return hash
    } catch (error: any) {
      if (isUserRejection(error)) {
        throw new Error('User rejected transaction')
      }
      console.error('Could not send token', error)
      throw error
    } finally {
      setIsPending(false)
    }
  }, [
    currency,
    parsedAmount,
    account,
    chainId,
    isValidRecipient,
    isNonTransferableToken,
    recipient,
    sendTransactionAsync,
    writeContractAsync,
    addTransaction,
  ])

  return useMemo(
    () => ({
      execute: inputError === SendTokenInputError.NO_ERROR ? execute : undefined,
      isPending,
      inputError,
    }),
    [execute, isPending, inputError]
  )
}
